import React, { Component, ErrorInfo } from 'react';
import { Button, Text } from '@vymo/ui/atoms';
import classnames from 'classnames';
import { ReactComponent as ErrorIcon } from 'src/assets/icons/error.svg';
import { isNativeMobileApp } from 'src/workspace/utils';
import styles from './index.module.scss';

interface Props {
  children: React.ReactNode;
  customMessage?: React.ReactNode;
  fullPage?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export interface PlatformInterfaceError extends Error {
  code?: string;
  type?: 'HostAppError' | 'APIError';
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });
  }

  private getErrorDetails = (error: PlatformInterfaceError) => {
    const { error: stateError } = this.state;

    if (stateError) {
      // Handle HostApp specific errors
      if (error.type === 'HostAppError') {
        return {
          code: 'HOSTAPP_ERROR',
          title: 'HostApp Interface Error',
          message: error.message,
        };
      }

      // Handle navigation errors
      if (error.message.includes('navigation')) {
        return {
          code: 'NAV_ERROR',
          title: 'Navigation Failed',
          message: error.message,
        };
      }

      // Default error
      return {
        code: 'CODE_ERROR',
        title: 'Something went wrong',
        message: error.message,
      };
    }

    return {
      code: 'UNKNOWN_ERROR',
      title: 'Something went wrong',
      message: 'An unknown error occurred',
    };
  };

  render() {
    const { children, fullPage, customMessage } = this.props;
    const { hasError, error, errorInfo } = this.state;

    const errorBoundaryClass = classnames(styles.errorBoundary, {
      [styles.errorBoundary__fullPage]: fullPage,
    });

    if (hasError && error) {
      const platformError = error as PlatformInterfaceError;
      const { code, title, message } = this.getErrorDetails(platformError);

      if (customMessage) {
        return customMessage;
      }

      return (
        <div className={errorBoundaryClass}>
          <div className={styles.errorBoundary__content}>
            <div className={styles.errorBoundary__title}>
              <ErrorIcon className={styles.errorBoundary__icon} />
              <Text type="h3">{title}</Text>
            </div>
            <div className={styles.errorBoundary__details}>
              {isNativeMobileApp() && (
                <Text variant="info">{window.location.href}</Text>
              )}
              <Text variant="error">Error Code: {code}</Text>

              <div className={styles.errorBoundary__message}>
                <Text>{message}</Text>
              </div>
              {errorInfo && (
                <details className={styles.errorBoundary__stack}>
                  <summary className={styles.errorBoundary__stackSummary}>
                    <Text>Technical Details</Text>
                  </summary>
                  <pre className={styles.errorBoundary__stackTrace}>
                    {errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
            <div className={styles.errorBoundary__actions}>
              <Button onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
